// client/src/features/quotations/linePriceApplication.ts
//
// PUTTING A PRICE ON A QUOTATION LINE, HOWEVER THE PRICE ARRIVED.
//
// Extracted from `QuotationDetailPage.handleSearchSelect`'s tail — the part
// that runs once a price, its currency and its unit have already been
// RESOLVED, whatever resolved them: a Master Catalogue pick (this file's
// first caller), or a person typing a price straight into the catalogue-
// addition form and having it priced immediately while the submission waits
// for review (`ItemLookupPanel`'s "+ Add this item to Master Catalogue").
//
// Two call sites reaching this exact point used to mean two copies of the
// same money computation — this codebase's #1 historical defect shape
// (CLAUDE.md's golden rule: duplicated `num()`s and unit normalizers). One
// function prices both now; `linePriceApplicationCataloguePickParity.test.ts`
// pins this file's output against the RECORDED output of the code it
// replaced, not a re-typed literal, so a future edit cannot drift the
// catalogue path while "fixing" the manual one.
//
// `ConversionAnchor` / `anchorFromItem` / `normalizeUnit` / `positiveNumber`
// moved here WITH the tail, out of `QuotationDetailPage.tsx` (a god file
// under a line ceiling, TECH_DEBT #26) — the anchor is exactly the part of
// "applying a price" this file now owns, and `normalizeUnit` is a unit
// normalizer, which CLAUDE.md names by name as the pattern never to
// duplicate. `QuotationDetailPage` imports them back rather than keeping a
// second copy. `positiveNumber` moved on again, to `utils/priceUtils.ts`
// (F12, 2026-09-25) — see its re-export below.

import type { QuotationItemDto } from '../../lib/quotationApi';
import { ceilPrice, positiveNumber } from '../../utils/priceUtils';
import { lockedUnitSellingPrice, markupCostUsd, priceLineAt } from '../../utils/pricing';
import { retiredPricingDetails } from '../../utils/quotationLinePricingFacts';
import { usdCostFacts } from '../../utils/fxDisplay';
import { lockedUnitRefusal, LockedLineError } from './statedSelling';

/** A unit exactly as the page has always normalized one: trimmed, upper-cased, nothing folded. */
export const normalizeUnit = (value?: string | null): string => String(value || '').trim().toUpperCase();

// `positiveNumber` moved to `utils/priceUtils.ts` (F12, 2026-09-25) — a
// `lib/` file needed it and `lib` must not depend on a `features/` folder.
// Re-exported so every existing `from './linePriceApplication'` import (this
// file's own test, `QuotationDetailPage`) keeps working unchanged.
export { positiveNumber };

/**
 * The line's own fixed point for the NEXT Unit change to restate from — the
 * unit, price and quantity it is anchored at right now, plus the two figures
 * a restatement carries forward unchanged (selling price, competitor price).
 *
 * TECH_DEBT #151: this answers "what was the line last ESTABLISHED as", not
 * "what did the RFQ actually ask for" — a hand-edited Quantity/Unit Cost
 * becomes the new baseline (it has to, or the next conversion restates the
 * correction away), so the two questions only share an answer until somebody
 * corrects the row. For the true, never-touched RFQ ask, read
 * `item.originalRequest` instead (written once at generation).
 */
export type ConversionAnchor = {
  uom: string;
  unitPrice: number;
  quantity: number | null;
  unitSellingPrice: number | null;
  competitorPrice: number | null;
};

/** The line as it stands, which is what a first conversion anchors to. */
export const anchorFromItem = (item: QuotationItemDto): ConversionAnchor | null => {
  // The UNROUNDED cost when it still explains the stored one (D85, TECH_DEBT #210).
  const unitPrice = positiveNumber(markupCostUsd(item.unitPrice, item.unitPriceExact));
  const uom = normalizeUnit(item.uom || 'PCS');
  if (unitPrice == null || !uom) return null;
  return {
    uom,
    unitPrice,
    quantity: positiveNumber(item.quantity),
    unitSellingPrice: positiveNumber(item.unitSellingPrice),
    competitorPrice: positiveNumber(item.competitorPrice),
  };
};

/** What a caller already knows once a price has a currency-resolved US$ figure. */
export interface LinePriceFacts {
  /** The line's new cost, in US$ — already resolved (`fxDisplay.usdCostFacts` - a TT$ price ceiled once - or a catalogue row's own per-unit figure). */
  unitPriceUsd: number;
  /** The TT$ this line's cost is PINNED at, when the price came in TT$ — never derived from `unitPriceUsd`. Null for a US$ price. */
  unitPriceTtd: number | null;
  /** The unit this line is now priced per. */
  uom: string;
  /** Whether this price carries VAT. */
  vat: boolean;
  /** The vendor/supplier name to show on the line, or falsy to clear it. */
  selectedVendor?: string | null;
  /**
   * How many of `uom` this line quotes. UNCHANGED by this function — a caller
   * that needs to restate a quantity through a different product's pack (the
   * catalogue pick's QUO-00054 rule) does that BEFORE calling in, and passes
   * the restated figure here.
   */
  quantity: number;
  /** The rate to price this line's markup at — typically `markupToApply(currentItem, housePercent)`, resolved by the caller. */
  markupPercent: number;
  /**
   * The UNROUNDED per-unit price to anchor the next Unit change from, when it
   * differs from `unitPriceUsd` (which may already be ceiled to the cent) —
   * see `anchorFromItem`'s QUO-00054 comment above. Omit, or pass a
   * non-positive number, to anchor at `unitPriceUsd` itself. D85: it is also the
   * line's stored `unitPriceExact`, the cost the markup is applied to.
   */
  exactUnitPriceUsd?: number | null;
}

/** What applying `LinePriceFacts` to a line produces. */
export interface AppliedLinePrice {
  /** Spread straight onto the line — every field a price touches, and nothing else. */
  line: {
    unitPrice: number;
    /** D85: the unrounded cost `unitPrice` was ceiled from (`markupCostUsd`). */
    unitPriceExact: number | null;
    unitPriceTtd: number | null;
    uom: string;
    vat: boolean;
    selectedVendor: string | undefined;
    quantity: number;
    totalPrice: number;
    markup: number | null;
    unitSellingPrice: number;
    totalSellingPrice: number;
  };
  /** The anchor this price was written with, or null when there is none to state (no positive price/uom). */
  conversionAnchor: ConversionAnchor | null;
  /**
   * `pricingDetails` with every PRICE/REFERENCE fact the replaced line carried
   * retired (`retiredPricingDetails`) and the new anchor set — usable AS IS by
   * a caller with nothing more specific to say (a manual entry). A caller
   * pricing from a known REFERENCE (a catalogue row) builds its own on top of
   * `conversionAnchor` instead — see
   * `cataloguePickDetails.pricingDetailsForCataloguePick`.
   */
  pricingDetails: Record<string, unknown>;
}

/**
 * Price a line at a set of already-resolved facts, and answer with the money,
 * the rate, and the conversion anchor together — never let a write site record
 * one without the others (the same discipline `priceLineAt` itself documents).
 *
 * `currentItem` is read, never mutated: `competitorPrice` and every
 * `pricingDetails` fact this function does not know about ride through from it
 * (the anchor computation needs the former; `retiredPricingDetails` needs the
 * latter). Nothing else of `currentItem` is touched — the caller decides the
 * rest of the line (name, `pricingSource`, catalogue flags, …).
 */
export function applyLinePriceFacts(
  currentItem: QuotationItemDto,
  facts: LinePriceFacts
): AppliedLinePrice {
  const quantity = facts.quantity;
  const qty = quantity || 0;
  const price = facts.unitPriceUsd || 0;
  const totalPrice = ceilPrice(qty * price);

  // D85: the markup goes on the exact cost, rounded up once.
  const exact = positiveNumber(facts.exactUnitPriceUsd) ?? positiveNumber(price);
  // D97: a locked line cannot be moved to another unit by ANY caller (a pick, a catalogue addition).
  const refusal = lockedUnitRefusal(currentItem, facts.uom);
  if (refusal) throw new LockedLineError(refusal);
  const { markup, unitSellingPrice, totalSellingPrice } = priceLineAt({
    unitCost: price,
    unitCostExact: exact,
    quantity: qty,
    markupPercent: facts.markupPercent,
    statedSellingPrice: lockedUnitSellingPrice(currentItem),
  });

  // Anchored off the line as it would read with this price applied — the same
  // shape `handleSearchSelect` used to anchor its own `newItem` at, once that
  // item's fields were set. `competitorPrice` rides through from `currentItem`
  // unchanged, same as a plain `{...currentItem}` spread would carry it.
  const draftForAnchor: QuotationItemDto = {
    ...currentItem,
    unitPrice: price,
    unitPriceExact: exact, // not the replaced line's: `anchorFromItem` reads it
    uom: facts.uom,
    quantity,
    unitSellingPrice,
  };
  const anchor = anchorFromItem(draftForAnchor);
  if (anchor && facts.exactUnitPriceUsd != null && facts.exactUnitPriceUsd > 0) {
    anchor.unitPrice = facts.exactUnitPriceUsd;
  }

  const pricingDetails: Record<string, unknown> = {
    ...retiredPricingDetails(currentItem.pricingDetails),
  };
  if (anchor) pricingDetails.conversionAnchor = anchor;

  return {
    line: {
      unitPrice: price,
      unitPriceExact: exact,
      unitPriceTtd: facts.unitPriceTtd,
      uom: facts.uom,
      vat: facts.vat,
      selectedVendor: facts.selectedVendor || undefined,
      quantity,
      totalPrice,
      markup,
      unitSellingPrice,
      totalSellingPrice,
    },
    conversionAnchor: anchor,
    pricingDetails,
  };
}

/** The cost state a hand edit of a line's cost states: the TT$ pin and the exact US$ cost. */
export type CostEdit = { unitPrice: number; unitPriceExact: number; unitPriceTtd: number | null };

/**
 * D85.6: what the TT$ cost cell stores for a TT$ figure typed by hand — the
 * same pair a generated line from a TT$ catalogue row gets (`usdCostFacts`:
 * cost ceiled once, the unrounded cost beside it for the markup), plus the
 * TT$ pin (display only: `unitCostTtd`). Pin and exact come from ONE number,
 * the typed figure ceiled to the cent. Null when the figure is not a number
 * or no rate has loaded — the cell then refuses.
 */
export const ttdCostEdit = (typed: unknown, ttdToUsd: number | null): CostEdit | null => {
  const ttd = typeof typed === 'number' ? typed : parseFloat(String(typed));
  if (!Number.isFinite(ttd)) return null;
  const ceiledTtd = ceilPrice(ttd);
  const facts = usdCostFacts(ceiledTtd, 'TTD', ttdToUsd);
  return facts ? { ...facts, unitPriceTtd: ttd >= 0 ? ceiledTtd : null } : null;
};

/**
 * The pin and exact cost a line carries after an edit of field `field`.
 * A TT$ cost edit (`costEdit`) SETS both; a hand edit of the US$ cost
 * RETIRES both (the person states the cost, the markup goes on it); any other
 * field leaves them alone. The one owner of what the grid's cost edits do to
 * `unitPriceTtd` / `unitPriceExact`.
 */
export const costFieldsAfterEdit = (
  field: string,
  costEdit?: CostEdit,
): { unitPriceTtd?: number | null; unitPriceExact?: number | null } => {
  if (costEdit) return { unitPriceTtd: costEdit.unitPriceTtd, unitPriceExact: costEdit.unitPriceExact };
  return field === 'unitPrice' ? { unitPriceTtd: null, unitPriceExact: null } : {};
};

/**
 * The cost a catalogue pick puts on a line: the popup's chosen price when
 * there is one (`priceSelection`), else the row's own cost — a TT$ row ceiled
 * once from the unrounded conversion (`usdCostFacts`), a US$ row as stated.
 * Null when there is no honest cost (no price, or no rate for a TT$ row).
 */
export const catalogueCostFacts = (args: {
  priceSelection?: { unitPriceUsd: number; unitPriceUsdExact: number } | null;
  costPrice: number | null;
  pricedTtd: boolean;
  ttdToUsd: number | null;
}): { unitPrice: number; unitPriceExact: number } | null => {
  const { priceSelection, costPrice, pricedTtd, ttdToUsd } = args;
  if (priceSelection) return { unitPrice: priceSelection.unitPriceUsd, unitPriceExact: priceSelection.unitPriceUsdExact };
  if (costPrice == null) return null;
  return usdCostFacts(costPrice, pricedTtd ? 'TTD' : 'USD', ttdToUsd);
};
